import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { maintenance } from '../services/api';
import {
  AlertTriangle, AlertOctagon, Package, IndianRupee,
  TrendingDown, RefreshCw, Settings as SettingsIcon, Info
} from 'lucide-react';

const URGENCY_CONFIG: Record<string, { label: string; color: string; soft: string; icon: React.ReactNode }> = {
  critical: { label: 'Critical', color: 'var(--accent-red)', soft: 'var(--accent-red-soft)', icon: <AlertOctagon size={13} /> },
  warning: { label: 'Warning', color: 'var(--accent-amber)', soft: 'var(--accent-amber-soft)', icon: <AlertTriangle size={13} /> },
  info: { label: 'Reorder', color: 'var(--accent-blue)', soft: 'var(--accent-blue-soft)', icon: <Info size={13} /> },
};

const Reorder: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(15);
  const [threshold, setThreshold] = useState(10);
  const [showSettings, setShowSettings] = useState(false);

  const load = () => {
    setLoading(true);
    maintenance.getReorder(days, threshold)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [days, threshold]);

  const inputStyle: React.CSSProperties = {
    padding: '9px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13,
    outline: 'none', fontFamily: 'inherit', width: 80, textAlign: 'center',
  };

  return (
    <Layout title="Reorder Suggestions" subtitle="Smart restocking based on sales velocity">
      {/* Controls */}
      <div className="animate-in" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setShowSettings(!showSettings)} style={{
            padding: '8px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}><SettingsIcon size={14} /> Settings</button>

          {showSettings && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Lookback</span>
                <input type="number" value={days} onChange={e => setDays(Math.max(1, Number(e.target.value)))} style={inputStyle} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>days</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Alert if stock lasts</span>
                <input type="number" value={threshold} onChange={e => setThreshold(Math.max(1, Number(e.target.value)))} style={inputStyle} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>days</span>
              </div>
            </div>
          )}
        </div>

        <button onClick={load} disabled={loading} style={{
          padding: '9px 18px', background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))',
          border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
        }}>
          <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Summary KPIs */}
      {data?.summary && (
        <div className="grid-4 animate-in-1" style={{ marginBottom: 22 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: 10 }}><Package size={16} /></div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Items to Reorder</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.5 }}>{data.summary.total_items}</div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
            <div style={{ color: 'var(--accent-red)', marginBottom: 10 }}><AlertOctagon size={16} /></div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Critical (≤3 days)</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-red)', letterSpacing: -0.5 }}>{data.summary.critical}</div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
            <div style={{ color: 'var(--accent-amber)', marginBottom: 10 }}><AlertTriangle size={16} /></div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Warning (≤7 days)</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-amber)', letterSpacing: -0.5 }}>{data.summary.warning}</div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
            <div style={{ color: 'var(--accent-green)', marginBottom: 10 }}><IndianRupee size={16} /></div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Est. Restock Cost</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-green)', letterSpacing: -0.5 }}>₹{data.summary.total_estimated_cost.toLocaleString('en-IN')}</div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="animate-in-1" style={{
        background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.15)',
        borderRadius: 'var(--radius-md)', padding: '12px 18px', marginBottom: 16,
        fontSize: 12.5, color: 'var(--accent-blue)', lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <TrendingDown size={16} style={{ flexShrink: 0 }} />
        <span>Based on the last <strong>{days} days</strong> of fulfilled orders. Items shown will run out within <strong>{threshold} days</strong> at current sell rate. Suggested qty = ~20 days of stock.</span>
      </div>

      {/* Suggestions Table */}
      <div className="animate-in-2" style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden',
      }}>
        <div className="table-responsive">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Urgency', 'Article', 'Size', 'Stock', 'Sold (period)', 'Avg/Day', 'Days Left', 'Suggested Qty', 'Est. Cost'].map(h => (
                  <th key={h} style={{
                    padding: '12px 16px', textAlign: h === 'Article' ? 'left' : 'right',
                    fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase',
                    letterSpacing: 0.8, fontWeight: 600,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.suggestions?.map((s: any) => {
                const uc = URGENCY_CONFIG[s.urgency] || URGENCY_CONFIG.info;
                return (
                  <tr key={s.inventory_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <span style={{
                        fontSize: 10.5, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
                        background: uc.soft, color: uc.color, display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>{uc.icon} {uc.label}</span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{s.color} {s.article_type}</div>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '2px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 600 }}>{s.size}</span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: s.current_stock <= 5 ? 'var(--accent-red)' : 'var(--text-primary)' }}>
                      {s.current_stock}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {s.sold_last_period}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: 'var(--accent-purple)', fontWeight: 600 }}>
                      {s.avg_daily_sales}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <span style={{
                        fontSize: 14, fontWeight: 800, letterSpacing: -0.3,
                        color: s.days_left <= 3 ? 'var(--accent-red)' : s.days_left <= 7 ? 'var(--accent-amber)' : 'var(--accent-blue)',
                      }}>{s.days_left}d</span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--accent-green)' }}>
                      {s.suggested_order}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--accent-amber)' }}>
                      ₹{s.estimated_cost.toLocaleString('en-IN')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(!data?.suggestions || data.suggestions.length === 0) && !loading && (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--accent-green)', fontSize: 14 }}>
            All stock levels are healthy — no reorder needed right now.
          </div>
        )}
        {loading && (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-muted)', fontSize: 13 }}>
            Calculating suggestions...
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
};

export default Reorder;
